import React, { createContext, useState, useEffect } from "react";
import { ShowToast, Severty } from "../helper/toast";
import { auctionsAPI } from "../services/api";

export const AuctionContext = createContext();

export const AuctionProvider = ({ children }) => {
  const [auctions, setAuctions] = useState([]);
  const [featuredAuctions, setFeaturedAuctions] = useState([]);
  const [currentAuction, setCurrentAuction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    filterType: "highBids", // highBids, newest, endingSoon, etc.
    status: "Open", // Open, Closed, etc.
    searchQuery: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Bid-related state
  const [userBids, setUserBids] = useState([]);
  const [auctionBids, setAuctionBids] = useState([]);
  const [userAuctions, setUserAuctions] = useState([]);

  // Load auctions when filters change
  // useEffect(() => {
  //   loadAuctions();
  // }, [filters, pagination.page]);

  const loadAuctions = async (params = {}) => {
    setLoading(true);
    try {
      // Build query parameters - only include them if they have values
      const queryParams = {};

      // Only add pagination if specified
      if (params.page !== undefined) {
        queryParams.page = params.page;
      }
      if (params.limit !== undefined) {
        queryParams.limit = params.limit;
      }

      // Only add filterType if specified (not for default "all auctions" call)
      if (params.filterType !== undefined) {
        queryParams.filterType = params.filterType;
      }

      // Only add status if specified
      if (params.status !== undefined) {
        queryParams.status = params.status;
      }

      // Only add search if specified
      if (params.search && params.search.trim() !== "") {
        queryParams.search = params.search;
      }

      // Add any additional params passed in
      Object.keys(params).forEach((key) => {
        if (
          !["page", "limit", "filterType", "status", "search"].includes(key) &&
          params[key] !== undefined
        ) {
          queryParams[key] = params[key];
        }
      });

      const response = await auctionsAPI.getAllAuctions(queryParams);
      if (response.status === 200) {
        setAuctions(response.data.items || response.data);
        setPagination((prev) => ({
          ...prev,
          total:
            response.data.pagination?.totalCount ||
            response.data.items?.length ||
            0,
          totalPages:
            response.data.pagination?.totalPages ||
            Math.ceil(
              (response.data.pagination?.totalCount ||
                response.data.items?.length ||
                0) / pagination.limit
            ),
        }));
        return { success: true, data: response.data };
      } else {
        ShowToast(response.message || "Failed to load auctions", Severty.ERROR);
        return {
          success: false,
          error: response.message || "Failed to load auctions",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load auctions. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loadAuctionById = async (auctionId) => {
    setLoading(true);
    try {
      const response = await auctionsAPI.getAuctionById(auctionId);

      if (response.status === 200 && response.data) {
        setCurrentAuction(response.data);
        return { success: true, data: response };
      } else {
        ShowToast(response.message || "Auction not found", Severty.ERROR);
        return {
          success: false,
          error: response.message || "Auction not found",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message || error.error || "Failed to load auction details.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const createAuction = async (auctionData) => {
    setLoading(true);
    try {
      const response = await auctionsAPI.createAuction(auctionData);

      if (response.status === 200 || response.status === 201) {
        // Refresh auctions list after creation
        await loadAuctions();
        ShowToast(
          response.message || "Auction created successfully!",
          Severty.SUCCESS
        );
        return { success: true, data: response };
      } else {
        ShowToast(
          response.message || "Failed to create auction",
          Severty.ERROR
        );
        return {
          success: false,
          error: response.message || "Failed to create auction",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to create auction. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateAuction = async (auctionId, auctionData) => {
    setLoading(true);
    try {
      const response = await auctionsAPI.updateAuction(auctionId, auctionData);

      if (response.status === 200) {
        // Update the auction in the current list
        setAuctions((prev) =>
          prev.map((auction) =>
            auction._id === auctionId
              ? { ...auction, ...response.data }
              : auction
          )
        );
        ShowToast(
          response.message || "Auction updated successfully!",
          Severty.SUCCESS
        );
        return { success: true, data: response };
      } else {
        ShowToast(
          response.message || "Failed to update auction",
          Severty.ERROR
        );
        return {
          success: false,
          error: response.message || "Failed to update auction",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to update auction. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const deleteAuction = async (auctionId) => {
    setLoading(true);
    try {
      const response = await auctionsAPI.deleteAuction(auctionId);

      if (response.status === 200) {
        // Remove the auction from the current list
        setAuctions((prev) =>
          prev.filter((auction) => auction._id !== auctionId)
        );
        ShowToast(
          response.message || "Auction deleted successfully!",
          Severty.SUCCESS
        );
        return { success: true, data: response };
      } else {
        ShowToast(
          response.message || "Failed to delete auction",
          Severty.ERROR
        );
        return {
          success: false,
          error: response.message || "Failed to delete auction",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to delete auction. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const placeBid = async (auctionId, bidAmount) => {
    setLoading(true);
    try {
      const response = await auctionsAPI.placeBid(auctionId, {
        amount: bidAmount,
      });

      if (response.status === 200 || response.status === 201) {
        // Refresh current auction to get updated bid information
        if (currentAuction && currentAuction._id === auctionId) {
          await loadAuctionById(auctionId);
        }
        // Refresh auctions list to update highest bid amounts
        await loadAuctions();
        ShowToast(
          response.message || "Bid placed successfully!",
          Severty.SUCCESS
        );
        return { success: true, data: response };
      } else {
        ShowToast(response.message || "Failed to place bid", Severty.ERROR);
        return {
          success: false,
          error: response.message || "Failed to place bid",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to place bid. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loadBids = async (auctionId, params = {}) => {
    setLoading(true);
    try {
      const response = await auctionsAPI.getAuctionBids(auctionId, params);

      if (response.status === 200 && response.data) {
        setAuctionBids(response.data.bids || response.data);
        return { success: true, data: response.data };
      } else {
        ShowToast(
          response.message || "Failed to load auction bids",
          Severty.ERROR
        );
        return {
          success: false,
          error: response.message || "Failed to load auction bids",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load auction bids. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loadUserAuctions = async (userId, params = {}) => {
    setLoading(true);
    try {
      const response = await auctionsAPI.getUserAuctions(userId, params);

      if (response.status === 200 && response.data) {
        setUserAuctions(response.data.auctions || response.data);
        return { success: true, data: response.data };
      } else {
        ShowToast(
          response.message || "Failed to load user auctions",
          Severty.ERROR
        );
        return {
          success: false,
          error: response.message || "Failed to load user auctions",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load user auctions. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loadUserBids = async (params = { filter: "OngoingBids" }) => {
    setLoading(true);
    try {
      const normalizedParams =
        typeof params === "string" ? { filter: params } : params;
      const response = await auctionsAPI.getUserBids(normalizedParams);
      if (response.status === 200) {
        return response;
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const searchAuctions = async (searchQuery) => {
    setLoading(true);
    try {
      const response = await auctionsAPI.searchAuctions(searchQuery);

      if (response.status === 200 && response.data) {
        setAuctions(response.data.auctions || response.data);
        return { success: true, data: response };
      } else {
        ShowToast(response.message || "No auctions found", Severty.INFO);
        return {
          success: false,
          error: response.message || "No auctions found",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message || error.error || "Search failed. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const clearCurrentAuction = () => {
    setCurrentAuction(null);
  };

  const clearAuctionBids = () => {
    setAuctionBids([]);
  };

  const clearUserData = () => {
    setUserAuctions([]);
    setUserBids([]);
  };

  return (
    <AuctionContext.Provider
      value={{
        // State
        auctions,
        featuredAuctions,
        currentAuction,
        loading,
        filters,
        pagination,

        // Bid State
        userBids,
        auctionBids,
        userAuctions,

        // Actions
        loadAuctions,
        loadAuctionById,
        createAuction,
        updateAuction,
        deleteAuction,
        placeBid,
        loadBids,
        loadUserAuctions,
        loadUserBids,
        searchAuctions,
        updateFilters,
        updatePagination,
        clearCurrentAuction,
        clearAuctionBids,
        clearUserData,

        // Setters
        setAuctions,
        setFeaturedAuctions,
        setCurrentAuction,
        setLoading,
        setFilters,
        setPagination,
        setUserBids,
        setAuctionBids,
        setUserAuctions,
      }}
    >
      {children}
    </AuctionContext.Provider>
  );
};

// Custom hook to use the auction context
export const useAuction = () => {
  const context = React.useContext(AuctionContext);
  if (context === undefined) {
    throw new Error("useAuction must be used within an AuctionProvider");
  }
  return context;
};
